import Image from 'app/Components/Shared/Image/image';
import React, {useState, useEffect, useRef, useCallback} from 'react';
import './HeroCarousel.scss';

const HeroCarousel = ({
  slides = [],
  autoPlay = true,
  autoPlayInterval = 5000,
  variant = 'image', // 'image' or 'video'
  className = '',
}) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const isDragging = useRef(false);
  const startPos = useRef(0);
  const currentTranslate = useRef(0);
  const prevTranslate = useRef(0);
  const animationId = useRef(null);
  const carouselRef = useRef(null);

  useEffect(() => {
    if (!autoPlay || slides.length <= 1) return;

    const interval = setInterval(() => setCurrentSlide((prev) => (prev + 1) % slides.length), autoPlayInterval);
    return () => clearInterval(interval);
  }, [autoPlay, autoPlayInterval, slides.length]);

  useEffect(() => {
    currentTranslate.current = currentSlide * getSlideWidth();
    prevTranslate.current = -currentSlide * getSlideWidth();
    // Ensure track position updates when slide changes
    if (carouselRef.current) {
      carouselRef.current.style.transform = `translateX(${currentSlide * 100}%)`;
    }
  }, [currentSlide]);

  const getSlideWidth = () => {
    return carouselRef.current ? carouselRef.current.offsetWidth : 0;
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const getPositionX = (event) => {
    return event.type.includes('mouse') ? event.pageX : event.touches[0].clientX;
  };

  const animation = useCallback(() => {
    if (carouselRef.current) carouselRef.current.style.transform = `translateX(${currentTranslate.current}px)`;
    if (isDragging.current) animationId.current = requestAnimationFrame(animation);
  }, []);

  const setSliderPosition = () => {
    const slideWidth = getSlideWidth();
    const threshold = slideWidth * 0.2; // 20% of slide width
    const moved = currentTranslate.current - prevTranslate.current;

    if (moved < threshold) {
      setCurrentSlide((s) => (s > 0 ? s - 1 : slides.length - 1));
    } else if (moved > -threshold) {
      setCurrentSlide((s) => (s < slides.length - 1 ? s + 1 : 0));
    }
    // If movement is less than threshold, stay on current slide
    // Reset translate values will happen in useEffect when currentSlide changes
  };

  // Touch events
  const handleTouchStart = (event) => {
    // Don't allow swiping if there's only one slide
    if (slides.length <= 1) return;
    
    isDragging.current = true;
    startPos.current = getPositionX(event);
    prevTranslate.current = currentSlide * getSlideWidth();
    currentTranslate.current = currentSlide * getSlideWidth();
    if (animationId.current) cancelAnimationFrame(animationId.current);
    animationId.current = requestAnimationFrame(animation);
  };

  const handleTouchMove = (event) => {
    // Don't allow swiping if there's only one slide
    if (slides.length <= 1) return;
    
    if (!isDragging.current) return;
    const currentPosition = getPositionX(event);
    const diff = currentPosition - startPos.current;
    currentTranslate.current = prevTranslate.current + diff;
  };

  const handleTouchEnd = () => {
    // Don't allow swiping if there's only one slide
    if (slides.length <= 1) return;
    
    isDragging.current = false;
    if (animationId.current) cancelAnimationFrame(animationId.current);
    setSliderPosition();
  };

  // Mouse events (for desktop drag support)
  const handleMouseDown = (event) => {
    // Don't allow swiping if there's only one slide
    if (slides.length <= 1) return;
    
    event.preventDefault();
    handleTouchStart(event);
  };

  const handleMouseMove = (event) => {
    // Don't allow swiping if there's only one slide
    if (slides.length <= 1) return;
    
    event.preventDefault();
    handleTouchMove(event);
  };

  const handleMouseUp = () => {
    // Don't allow swiping if there's only one slide
    if (slides.length <= 1) return;
    
    handleTouchEnd();
  };

  const handleMouseLeave = () => isDragging.current && handleTouchEnd();

  // Cleanup RAF on unmount to avoid leaks and conflicting animations
  useEffect(() => {
    return () => {
      if (animationId.current) cancelAnimationFrame(animationId.current);
    };
  }, []);

  if (!slides || slides.length === 0) {
    return null;
  }

  return (
    <div className={`hero-carousel ${variant} ${className}`}>
      <div className='carousel-container'>
        <div
          ref={carouselRef}
          className='carousel-track'
          style={{
            transform: `translateX(${currentSlide * 100}%)`,
            transition: 'transform 0.5s ease-in-out',
          }}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}>
          {slides.map((slide, index) => (
            <div key={index} className={`carousel-slide ${slide.variant || 'image'}`}>
              <div className='slide-image'>
                {slide.variant === 'video' ? (
                  <video
                    src={slide.image}
                    autoPlay
                    loop
                    muted
                    playsInline
                    webkit-playsinline="true"
                    disablePictureInPicture
                    controlsList="nodownload nofullscreen noremoteplayback"
                    style={{width: '100%', height: '100%', objectFit: 'cover', pointerEvents: 'none'}}
                  />
                ) : (
                  <Image src={slide.image} alt={slide.title} draggable={false} />
                )}
              </div>
              <div className='slide-content'>
                <span className='slide-title'>{slide.title}</span>
                {slide.subtitle && <span className='slide-subtitle'>{slide.subtitle}</span>}
              </div>
            </div>
          ))}
        </div>
      </div>

      {slides.length > 1 && (
        <div className='carousel-indicators'>
          {slides.map((_, index) => (
            <div
              key={index}
              className={`indicator ${index === currentSlide ? 'active' : ''}`}
              onClick={() => goToSlide(index)}
              role='button'
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  goToSlide(index);
                }
              }}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default React.memo(HeroCarousel);
